import React, { useContext, useState } from 'react';
import { Prompt } from 'react-router';
import styled from 'styled-components';
import { Form, Icon, InputNew as Input, Switch, Text, Title } from 'components';
import { AuthContext } from 'contexts';
import { ROLES } from '@bb/users';
import useSingleAccessEnabled from 'hooks/useSingleAccessEnabled';
import { Divider, StyledSwitchGrid } from './components';
import FormContext from './FormContext';
import { SUPRA_PIN_LENGTH } from './constants';
import { SubscriptionText } from '../MLSUserPage/styles';

/**
 * The tab to display a user's Supra Subscription settings.
 */
const SupraSubscriptionTab = () => {
  const { supraSubscription, setSupraSubscription, userState } =
    useContext(FormContext);

  const { auth } = React.useContext(AuthContext);

  const [pinVisible, setPinVisible] = useState(false);

  const isAdmin = ROLES[auth.role] >= ROLES.BRANCH_ADMIN;

  const isValidUser = userState?.id?.length !== 0;

  const isSingleAccessEnabledForBoard = useSingleAccessEnabled({
    userId: userState?.id,
  });

  const checkNumericInput = (input: string) => {
    const reg = /^\d{0,4}$/;
    return reg.test(input);
  };

  const handlePINChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (checkNumericInput(value)) {
      setSupraSubscription(subscription => ({
        ...subscription,
        currentPin: value,
        dirty: true,
      }));
    }
  };

  const handleNewPinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (checkNumericInput(value)) {
      setSupraSubscription(subscription => ({
        ...subscription,
        newPin: value,
        dirty: true,
      }));
    }
  };

  const handleConfirmingPinChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;
    if (checkNumericInput(value)) {
      setSupraSubscription(subscription => ({
        ...subscription,
        confirmingPin: value,
        dirty: true,
      }));
    }
  };

  const shouldHidePin = () => {
    // only mls admin can view the pin
    if (isAdmin && isValidUser) {
      return true;
    }
    return false;
  };

  return (
    <Form layout="vertical">
      <Prompt
        when={Boolean(supraSubscription.dirty)}
        message="Are you sure you want to leave without saving?"
      />
      <div
        style={{
          display: shouldHidePin() ? 'none' : 'visible',
        }}
      >
        <Title level={3} hideMargin>
          Mobile key serial number & PIN
        </Title>
        <StyledDescription>
          {userState?.supraSubscription?.keySerialNumber}
        </StyledDescription>
        {userState?.supraSubscription?.supraPin && (
          <SupraPinContainer>
            <SubscriptionText>PIN:</SubscriptionText>{' '}
            <StyledPinInput
              disabled
              type={pinVisible ? 'text' : 'password'}
              value={userState?.supraSubscription?.supraPin}
            />
            <Icon
              onClick={() => setPinVisible(prev => !prev)}
              type={pinVisible ? 'eye' : 'antdEyeInvisibleOutlined'}
            />
          </SupraPinContainer>
        )}
        <Divider />
        <Title level={3} hideMargin>
          Change your PIN
        </Title>
        <StyledDescription>
          Your PIN gives you secure access to Supra lockboxes. Please memorize
          your PIN or keep it in a safe place. If changing your PIN, your PIN
          must be 4 numeric digits.
        </StyledDescription>
        <StyledContainer>
          <StyledInputContainer>
            <Text>Current PIN</Text>
            <Input
              data-cy="supra-subscription-current-PIN-input"
              placeholder="Enter your current PIN"
              value={supraSubscription.currentPin}
              onChange={handlePINChange}
              maxLength={SUPRA_PIN_LENGTH}
            />
          </StyledInputContainer>
        </StyledContainer>
        <StyledContainer>
          <StyledInputContainer>
            <Text>New PIN</Text>
            <Input.Password
              data-cy="supra-subscription-new-PIN-input"
              placeholder="Enter your new PIN"
              value={supraSubscription.newPin}
              onChange={handleNewPinChange}
              maxLength={SUPRA_PIN_LENGTH}
            />
          </StyledInputContainer>
          <StyledInputContainer>
            <Text>Confirm New PIN</Text>
            <Input.Password
              data-cy="supra-subscription-confirming-PIN-input"
              placeholder="Enter your new PIN"
              value={supraSubscription.confirmingPin}
              onChange={handleConfirmingPinChange}
              maxLength={SUPRA_PIN_LENGTH}
            />
          </StyledInputContainer>
        </StyledContainer>
      </div>
      <Divider />
      <Title level={3}>Notifications</Title>
      <StyledSwitchGrid>
        <Switch
          checked={supraSubscription.showingStarted}
          onChange={checked =>
            setSupraSubscription(subscription => ({
              ...subscription,
              showingStarted: checked,
              dirty: true,
            }))
          }
        />
        Notify me of lockbox opening to start showing
        <Switch
          checked={supraSubscription.lockboxAssignmentReminder}
          onChange={checked =>
            setSupraSubscription(subscription => ({
              ...subscription,
              lockboxAssignmentReminder: checked,
              dirty: true,
            }))
          }
        />
        Notify me about lockbox assignment reminders
      </StyledSwitchGrid>

      {isSingleAccessEnabledForBoard && (
        <>
          <Title level={3}>Managed Access</Title>
          <StyledSwitchGrid>
            <Switch
              checked={supraSubscription.singleAccessEnabled}
              onChange={checked =>
                setSupraSubscription(subscription => ({
                  ...subscription,
                  singleAccessEnabled: checked,
                  dirty: true,
                }))
              }
            />
            Allow user to grant Single Access
          </StyledSwitchGrid>
        </>
      )}
    </Form>
  );
};

export default SupraSubscriptionTab;

const SupraPinContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: 6px;
  color: #595959;
  margin-bottom: 10px;
`;

const StyledPinInput = styled.input`
  background: none !important;
  border: 0;
  padding: 0;
  color: #000;
  width: 50px;
`;

const StyledDescription = styled(Text)`
  margin: 10px 0 10px 0;
  display: inline-block;
  margin-bottom: 20px;
`;

const StyledInputContainer = styled.div`
  width: 40%;
`;

const StyledContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;
